import React, { useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Button, Modal, Switch, Table, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
// PERMISSION BUNDLES
import type { BundlePlanGroup, PermissionBundle, PermissionBundleGroup } from "./permissionBundles";
import type { PermissionDescriptions } from "./RolesPermissionsCards";

interface BundleRow {
  key: string;
  group: PermissionBundleGroup;
  fromBundle?: PermissionBundle;
  /** Rows the group's first cell spans; 0 on every other row of the group (antd rowSpan). */
  groupSpan: number;
  description?: string;
}

/**
 * The confirmation shown when a sidebar switch with a permission bundle is
 * turned ON or OFF (see permissionBundles.ts). It lists every permission the
 * switch is about to change, each with its own switch already ON, so the
 * operator can accept the lot with one click or drop the ones they do not want.
 *
 * "ON" in a row always means "this permission WILL be changed": granted when
 * enabling, turned off when disabling. Only the keys still switched on come
 * back through `onConfirm` — the parent applies those together with the gate.
 * The selection lives here and is initialised from the plan on mount, so the
 * parent mounts this only while a confirmation is open: every opening starts
 * from "all on".
 *
 * When disabling, permissions another enabled block still needs are never
 * listed as switchable — they are shown as kept, with who needs them.
 */
function BundlePermissionsModal({
  mode,
  name,
  groups,
  unavailable = [],
  kept = [],
  alsoOff = [],
  labelOf,
  descriptions = {},
  onConfirm,
  onCancel,
}: {
  mode: "enable" | "disable";
  name: string;
  groups: BundlePlanGroup[];
  unavailable?: string[];
  kept?: Array<{ treeKey: string; heldBy: string[] }>;
  alsoOff?: string[];
  labelOf: (bundle: PermissionBundle) => string;
  descriptions?: PermissionDescriptions;
  onConfirm: (selectedTreeKeys: string[]) => void;
  onCancel: () => void;
}) {
  const [t] = useTranslation("global");
  const enabling = mode === "enable";

  const rows = useMemo<BundleRow[]>(
    () =>
      groups.flatMap(({ group, treeKeys, fromBundle }) =>
        treeKeys.map((treeKey, index) => ({
          key: treeKey,
          group,
          fromBundle,
          groupSpan: index === 0 ? treeKeys.length : 0,
          description: descriptions[treeKey],
        })),
      ),
    [groups, descriptions],
  );

  const [selected, setSelected] = useState<Set<string>>(() => new Set(rows.map((row) => row.key)));

  const [showDetails, setShowDetails] = useState(false);

  const total = rows.length;
  const selectedCount = selected.size;

  const setOne = (treeKey: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(treeKey);
      else next.delete(treeKey);
      return next;
    });

  const setAll = (checked: boolean) =>
    setSelected(checked ? new Set(rows.map((row) => row.key)) : new Set());

  const groupCount = (group: PermissionBundleGroup) => {
    const inGroup = rows.filter((row) => row.group.id === group.id);
    return `${inGroup.filter((row) => selected.has(row.key)).length}/${inGroup.length}`;
  };

  const columns = [
    {
      title: t("organizationManagement.bundleColArea") || "Area",
      key: "area",
      width: 210,
      onCell: (row: BundleRow) => ({ rowSpan: row.groupSpan }),
      render: (_: unknown, row: BundleRow) =>
        row.groupSpan === 0 ? null : (
          <div className="bundleModal__area">
            <div className="bundleModal__areaTitle">
              <span>
                {t(`organizationManagement.bundles.${row.group.id}.label`) ||
                  row.group.fallbackLabel}
              </span>
              <Tag color={groupCount(row.group).startsWith("0/") ? "default" : "blue"}>
                {groupCount(row.group)}
              </Tag>
            </div>
            <div className="bundleModal__areaReason">
              {t(`organizationManagement.bundles.${row.group.id}.reason`) ||
                row.group.fallbackReason}
            </div>
            {row.fromBundle && (
              <Tag className="bundleModal__fromTag">
                {enabling
                  ? t("organizationManagement.bundleRequiresTag", {
                      name: labelOf(row.fromBundle),
                    })
                  : t("organizationManagement.bundleDependentTag", {
                      name: labelOf(row.fromBundle),
                    })}
              </Tag>
            )}
          </div>
        ),
    },
    {
      title: t("organizationManagement.permission") || "Permission",
      key: "permission",
      render: (_: unknown, row: BundleRow) => (
        <div>
          <code className="bundleModal__key">{row.key}</code>
          {row.description && <div className="bundleModal__description">{row.description}</div>}
        </div>
      ),
    },
    {
      title: (
        <div className="bundleModal__enabledHeader">
          <span>
            {enabling
              ? t("organizationManagement.bundleColEnabled") || "Enabled"
              : t("organizationManagement.bundleColTurnOff") || "Turn off"}
          </span>
          <Switch
            size="small"
            checked={total > 0 && selectedCount === total}
            onChange={setAll}
            aria-label={t("organizationManagement.bundleToggleAll") || "Toggle all permissions"}
          />
        </div>
      ),
      key: "enabled",
      width: 130,
      align: "center" as const,
      render: (_: unknown, row: BundleRow) => (
        <Switch
          size="small"
          checked={selected.has(row.key)}
          onChange={(checked) => setOne(row.key, checked)}
          aria-label={row.key}
        />
      ),
    },
  ];

  const counterLabel = enabling
    ? t("organizationManagement.bundleEnabledLabel") || "enabled"
    : t("organizationManagement.bundleTurnOffLabel") || "to turn off";

  const footerNote = (() => {
    if (total === 0) return "";
    if (selectedCount === total) {
      return enabling
        ? t("organizationManagement.bundleFooterAll") || "Every permission will be enabled."
        : t("organizationManagement.bundleFooterAllOff") ||
            "Every listed permission will be turned off.";
    }
    const left = total - selectedCount;
    return enabling
      ? `${left} ${
          t("organizationManagement.bundleFooterSkipped") ||
          "permission(s) will be left off, and parts of the screen may not work."
        }`
      : `${left} ${t("organizationManagement.bundleFooterKept") || "permission(s) will stay on."}`;
  })();

  const badge = (
    <span className={`bundleModal__badge ${enabling ? "" : "bundleModal__badge--off"}`.trim()}>
      <FontAwesomeIcon icon={enabling ? faShieldHalved : faPowerOff} />
    </span>
  );

  const titleText = (
    <div className="bundleModal__title">
      {enabling
        ? t("organizationManagement.bundleModalTitle", { name }) || `Enable ${name}`
        : t("organizationManagement.bundleDisableTitle", { name }) || `Turn off ${name}`}
    </div>
  );

  const subtitleText = (
    <div className="bundleModal__subtitle">
      {enabling
        ? t("organizationManagement.bundleModalSubtitle") ||
          "This block needs the permissions below to work. All of them are switched on — turn off any you do not want to grant."
        : t("organizationManagement.bundleDisableSubtitle") ||
          "No other enabled block needs the permissions below, so they will be turned off. Switch off any you want to keep."}
    </div>
  );

  const counter = total > 0 && (
    <Tag color={enabling ? "blue" : "red"} className="bundleModal__counter">
      {selectedCount}/{total} {counterLabel}
    </Tag>
  );

  return (
    <Modal
      open
      centered
      width={showDetails ? 780 : 520}
      closable={false}
      maskClosable={false}
      onCancel={onCancel}
      className={showDetails ? "bundleModal" : "bundleModal bundleModal--summary"}
      title={
        showDetails ? (
          <div className="bundleModal__header">
            {badge}
            <div className="bundleModal__headerText">
              {titleText}
              {subtitleText}
            </div>
            {counter}
          </div>
        ) : null
      }
      footer={
        <div className="bundleModal__footer">
          <span className="bundleModal__footerNote">{footerNote}</span>
          <div className="bundleModal__footerButtons">
            <Button onClick={onCancel}>{t("general.cancel") || "Cancel"}</Button>
            <Button
              type="primary"
              danger={!enabling}
              onClick={() => onConfirm(Array.from(selected))}
            >
              {enabling
                ? t("organizationManagement.bundleConfirmOk") || "Enable selected"
                : t("organizationManagement.bundleDisableOk") || "Turn off selected"}
            </Button>
          </div>
        </div>
      }
    >
      {!showDetails && (
        <div className="bundleModal__summary">
          {badge}
          {titleText}
          {subtitleText}
          {counter}
        </div>
      )}
      {alsoOff.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="bundleModal__alert"
          message={
            t("organizationManagement.bundleAlsoOff") ||
            "Also turning off, because they need this block:"
          }
          description={alsoOff.join(", ")}
        />
      )}
      {unavailable.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="bundleModal__alert"
          message={
            t("organizationManagement.bundleUnavailable") ||
            "Not available in your own permissions, so they will be skipped:"
          }
          description={unavailable.join(", ")}
        />
      )}
      {kept.length > 0 && (
        <Alert
          type="info"
          showIcon
          className="bundleModal__alert"
          message={
            t("organizationManagement.bundleKeptShared") ||
            "Staying on, because another enabled block still needs them:"
          }
          description={kept
            .map(({ treeKey, heldBy }) => `${treeKey} (${heldBy.join(", ")})`)
            .join("; ")}
        />
      )}
      {!showDetails && total > 0 && (
        <div className="bundleModal__seeDetails">
          <Button onClick={() => setShowDetails(true)}>
            {t("organizationManagement.bundleSeeDetails") || "See details"}
          </Button>
        </div>
      )}
      {showDetails && total > 0 && (
        <Table<BundleRow>
          size="small"
          bordered
          pagination={false}
          rowKey="key"
          columns={columns}
          dataSource={rows}
          scroll={{ y: "calc(80vh - 300px)" }}
          className="bundleModal__table"
        />
      )}
    </Modal>
  );
}

export default BundlePermissionsModal;
